import { QuantitySelectorUpdateEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

// Dispatched (bubbling) by the offer app's widget each time the shopper picks an offer, with the
// total of what the add-to-cart button will add in `detail.pricing.discountedPrice` (cents and
// formatted string). Undocumented by the app, read from its widget on 15/10/2026: if it changes,
// nothing breaks, the bands simply keep the product's own price as before.
const APP_OFFER_CHANGE_EVENT = 'variants-changed';

/**
 * Offer tiers of the product buy column: the checked tier sets the quantity of Horizon's native
 * product form, which stays the only path to the cart. The quantity is pushed on every tier
 * change and re-applied at submit time (capture phase, before product-form reads it), so a
 * variant change that re-renders the stepper can never add the wrong quantity. Tier totals
 * follow the selected size through the product HTML Horizon fetches on each variant change.
 * Tiers render disabled: this script enables those the native quantity rules can reach (min,
 * increment, max left once the cart is counted) and checks the default one, so a tier is never
 * clamped under its displayed total. Changes to her selection the shopper did not make (new totals
 * after a size change, a checked tier replaced because it became unreachable) are announced in the
 * group's status region, the native stepper being hidden.
 * Scoped to the product section: the quick-add dialog hides the tiers and keeps the native stepper.
 * One group per section: every group drives the same stepper, and a theme block schema cannot cap
 * how many times the merchant adds it. A second group is set aside: hidden on the storefront,
 * disabled with a warning in the theme editor.
 * One offer picker per product: when an app already carries this product's offers (the element
 * named by the block's setting is on the page, filled, for this product), the group steps aside
 * and that app owns quantity, discount and price, so the shopper never reads two sets of offers.
 * The bands that describe what the button adds (installments, sticky bar price) then follow the
 * total of the offer checked in the app, never the product's single price.
 */
class ProductOfferTiers extends HTMLElement {
  #section = null;
  #appOfferTotal = null;
  #constraintsObserver = new MutationObserver(() => this.#syncTiers());

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    if (this.#section && this.#section.querySelector('product-offer-tiers') !== this) {
      this.#setAside({ warn: true });
      return;
    }
    if (this.#hasAppOffers()) {
      this.#setAside();
      this.#section?.addEventListener(APP_OFFER_CHANGE_EVENT, this.#followAppOffer);
      this.#section?.addEventListener(StandardEvents.productSelect, this.#keepAppOfferAfterVariant);
      return;
    }
    this.addEventListener('change', this.#applyQuantity);
    this.#section?.addEventListener('submit', this.#applyQuantity, { capture: true });
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    // The stepper's rules change on variant change and after each add to cart (data-cart-quantity).
    if (this.#section) {
      this.#constraintsObserver.observe(this.#section, {
        subtree: true,
        attributeFilter: ['min', 'max', 'step', 'data-cart-quantity'],
      });
    }
    this.#syncTiers();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#applyQuantity);
    this.#section?.removeEventListener('submit', this.#applyQuantity, { capture: true });
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section?.removeEventListener(APP_OFFER_CHANGE_EVENT, this.#followAppOffer);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#keepAppOfferAfterVariant);
    this.#constraintsObserver.disconnect();
  }

  /**
   * The app's markup is server-rendered for every product and left empty when it holds no offer,
   * so only a filled element counts. An element tied to another product is ignored.
   */
  #hasAppOffers() {
    const selector = this.dataset.appOffersSelector;
    if (!selector) return false;
    return [...document.querySelectorAll(selector)].some((element) => {
      const productId = element.dataset.productId;
      return (!productId || productId === this.dataset.productId) && element.textContent.trim() !== '';
    });
  }

  #setAside({ warn = false } = {}) {
    for (const radio of this.#radios) {
      radio.checked = false;
      radio.disabled = true;
    }
    const warning = this.querySelector('[data-duplicate-warning]');
    if (warn && window.Shopify?.designMode && warning) {
      warning.hidden = false;
    } else {
      this.hidden = true;
    }
  }

  get #radios() {
    return [...this.querySelectorAll('.product-offer-tier__radio')];
  }

  get #selector() {
    return this.#section?.querySelector('product-form-component quantity-selector-component') ?? null;
  }

  #syncTiers({ announce = false } = {}) {
    const selector = this.#selector;
    const focused = this.#radios.find((radio) => radio === document.activeElement);
    if (selector) {
      const { min, step } = selector.getCurrentValues();
      const max = selector.getEffectiveMax();
      for (const radio of this.#radios) {
        const quantity = Number(radio.value);
        radio.disabled = quantity < min || (quantity - min) % step !== 0 || (max !== null && quantity > max);
      }
    }
    const replaced = this.#ensureSelection();
    this.#applyQuantity();
    if (announce || replaced) this.#announce();
    // A disabled radio drops keyboard focus to the page: hand it to the tier that replaced it, or
    // to the native stepper, shown again when no tier is reachable.
    if (focused?.disabled) {
      const target = this.#radios.find((radio) => radio.checked) ?? selector?.querySelector('input');
      target?.focus();
    }
    this.#syncCtaPrice();
  }

  /**
   * Parité maquette (FICHE-CTA-AVEC-PRIX) : le bouton « Ajouter au panier » natif et la barre
   * d'achat collante mobile ne portent aucun prix. On pousse le total du palier coché (déjà
   * calculé côté serveur dans `[data-tier-dynamic="price"]`, jamais recalculé en JS) dans :
   * (1) un badge de prix injecté une seule fois dans chaque bouton `.add-to-cart-button` de la
   * section (le bouton natif n'est jamais réécrit entièrement, cette injection survit donc aux
   * changements d'état du bouton) ; (2) le titre et le prix déjà présents dans la barre collante
   * native d'Horizon (`.sticky-add-to-cart__title` / `__price`), dont le titre est ici aussi
   * ramené au nom court (même règle que la fiche, sans toucher le fichier Horizon).
   */
  #syncCtaPrice() {
    const tier = this.#radios.find((radio) => radio.checked)?.closest('[data-tier]');
    if (!tier || !this.#section) return;
    const price = tier.querySelector('[data-tier-dynamic="price"]')?.textContent.trim();
    const compare = tier.dataset.tierCompareAmount || '';
    const label = tier.dataset.tierLabel || '';
    if (!price) return;

    for (const button of this.#section.querySelectorAll('.add-to-cart-button')) {
      if (button.closest('sticky-add-to-cart')) continue;
      let badge = button.querySelector('.product-offer-tiers__cta-price');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'product-offer-tiers__cta-price';
        const added = button.querySelector('.add-to-cart__added');
        if (added) added.before(badge);
        else button.append(badge);
      }
      badge.replaceChildren();
      if (compare) {
        const strike = document.createElement('span');
        strike.className = 'product-offer-tiers__cta-compare';
        strike.textContent = compare;
        badge.append(strike);
      }
      badge.append(document.createTextNode(price));
    }

    const stickyPrice = this.#section.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice) stickyPrice.textContent = price;

    // Bandeau « paiement en 3 fois » (product-payment-installments.liquid) : montant déjà calculé
    // et formaté côté serveur pour ce palier (data-tier-dynamic="installment" dans
    // _product-offer-tier.liquid), jamais recalculé ici.
    const installment = tier.querySelector('[data-tier-dynamic="installment"]')?.textContent.trim();
    const installmentAmount = this.#section.querySelector('[data-installment-amount]');
    if (installmentAmount && installment) installmentAmount.textContent = installment;
    // Parité maquette (barre collante mobile, produits.$slug.tsx l. 713-719) : la barre affiche
    // « Ajouter au panier · {palier} · {prix} », le palier remplaçant le libellé produit/variante
    // natif dans ce créneau — jamais le titre complet suffixé.
    const stickyTitle = this.#section.querySelector('.sticky-add-to-cart__title');
    if (stickyTitle && label) {
      stickyTitle.textContent = label;
    }
  }

  /**
   * Mirrors the app's checked offer into the installments band and the sticky bar price. The
   * installment is rounded up to the cent, the rule of `snippets/brand-installment-amount.liquid`
   * that renders the same band server-side.
   */
  #followAppOffer = (event) => {
    const total = event.detail?.pricing?.discountedPrice;
    if (!Number.isFinite(total?.amount) || !total.formatted) return;
    this.#appOfferTotal = total;
    this.#renderAppOffer();
  };

  /**
   * A size change re-renders the sticky bar from the server (`sticky-add-to-cart.js` morphs it in
   * the same promise callback), back to the single variant's price: the app's last total is put
   * back on the next frame, once every callback of that promise has run.
   */
  #keepAppOfferAfterVariant = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(() => requestAnimationFrame(() => this.#renderAppOffer()))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-offer-tiers] Event promise rejected:', error);
      });
  };

  #renderAppOffer() {
    const total = this.#appOfferTotal;
    if (!this.#section || !total) return;

    const stickyPrice = this.#section.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice) stickyPrice.textContent = total.formatted;

    const installment = this.#section.querySelector('[data-installment-amount]');
    const count = Number(installment?.dataset.installmentsCount);
    if (!installment || !(count > 0)) return;
    const { moneyFormat, currency } = installment.dataset;
    installment.textContent = formatMoney(Math.ceil(total.amount / count), moneyFormat, currency);
  }

  #announce() {
    const status = this.querySelector('[data-tier-status]');
    const tier = this.#radios.find((radio) => radio.checked)?.closest('[data-tier]');
    if (!status || !tier) return;
    const label = tier.querySelector('.product-offer-tier__label')?.textContent.trim();
    const price = tier.querySelector('[data-tier-dynamic="price"]')?.textContent.trim();
    status.textContent = [label, price].filter(Boolean).join(', ');
  }

  /**
   * Keeps one reachable tier checked: the default one (the last one set, as the setting says, else
   * the first in editor order), or, when the checked tier became unreachable, the largest reachable
   * quantity below it.
   * @returns {boolean} whether a checked tier was replaced
   */
  #ensureSelection() {
    const radios = this.#radios;
    const checked = radios.find((radio) => radio.checked);
    if (checked && !checked.disabled) return false;

    const reachable = radios.filter((radio) => !radio.disabled);
    let next = reachable.findLast((radio) => radio.hasAttribute('data-default-selected')) ?? reachable[0];
    if (checked) {
      checked.checked = false;
      const below = reachable.filter((radio) => Number(radio.value) < Number(checked.value));
      next = below.sort((a, b) => Number(a.value) - Number(b.value)).at(-1) ?? next;
    }
    if (next) next.checked = true;
    return Boolean(checked);
  }

  #applyQuantity = () => {
    const checked = this.#radios.find((radio) => radio.checked && !radio.disabled);
    const selector = this.#selector;
    if (!checked || !selector) return;

    const quantity = Number(checked.value) || 1;
    if (Number(selector.getValue()) === quantity) return;

    // The tier is reachable (#syncTiers), so re-applying the input's own constraints never alters
    // the quantity: it only refreshes the native buttons the way a typed value would.
    const { min, max, step } = selector.getCurrentValues();
    selector.setValue(String(quantity));
    selector.updateConstraints(String(min), max === null ? null : String(max), String(step));
    selector.dispatchEvent(new QuantitySelectorUpdateEvent(Number(selector.getValue())));
    this.#syncCtaPrice();
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const fresh = detail.html.querySelector(`product-offer-tiers[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;

        for (const tier of this.querySelectorAll('[data-tier]')) {
          const freshTier = fresh.querySelector(`[data-tier="${tier.dataset.tier}"]`);
          if (!freshTier) continue;
          for (const part of tier.querySelectorAll('[data-tier-dynamic]')) {
            const freshPart = freshTier.querySelector(`[data-tier-dynamic="${part.dataset.tierDynamic}"]`);
            if (freshPart) part.innerHTML = freshPart.innerHTML;
          }
        }
        this.#syncTiers({ announce: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-offer-tiers] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-offer-tiers')) {
  customElements.define('product-offer-tiers', ProductOfferTiers);
}
